'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Reveal } from '../Reveal'
import { PhoneIcon, WhatsappIcon, MailIcon, MapPinIcon, CheckIcon, ArrowIcon } from '../Icons'
import { submitContact } from '@/lib/api'
import { contact } from '@/lib/site-config'

type Status = 'idle' | 'loading' | 'success' | 'error'

interface MessageState {
  name: string
  email: string
  message: string
}

const initial: MessageState = { name: '', email: '', message: '' }

export function Contact(): React.ReactElement {
  const [form, setForm] = useState<MessageState>(initial)
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        subject: 'رسالة من نموذج تواصل معنا',
        message: form.message,
      })
      setStatus('success')
      setForm(initial)
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'حدث خطأ غير متوقع')
    }
  }

  const cards = [
    {
      icon: <PhoneIcon className="h-6 w-6" />,
      label: 'الهاتف',
      value: contact.phoneDisplay,
      href: `tel:${contact.phoneHref}`,
    },
    {
      icon: <WhatsappIcon className="h-6 w-6" />,
      label: 'واتساب',
      value: contact.whatsappDisplay,
      href: `https://wa.me/${contact.whatsappNumber}`,
    },
    {
      icon: <MailIcon className="h-6 w-6" />,
      label: 'البريد الإلكتروني',
      value: contact.email,
      href: `mailto:${contact.email}`,
    },
    {
      icon: <MapPinIcon className="h-6 w-6" />,
      label: 'الموقع',
      value: contact.location,
      href: undefined,
    },
  ]

  return (
    <section id="contact" className="section-pad relative overflow-hidden bg-charcoal-950">
      <div className="absolute -top-20 left-1/4 h-72 w-72 rounded-full bg-gold-500/10 blur-3xl" />
      <div className="container-x relative">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="eyebrow mb-5">تواصل معنا</span>
          <h2 className="font-heading text-3xl font-extrabold text-white sm:text-4xl md:text-5xl">
            نحن هنا <span className="gold-text">لخدمتك</span>
          </h2>
          <p className="mt-5 text-lg text-charcoal-300">
            تواصل معنا عبر أي وسيلة تناسبك، وسنكون سعداء بالرد على استفساراتك.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          {/* Contact info + actions */}
          <div>
            <div className="grid gap-4 sm:grid-cols-2">
              {cards.map((card, i) => {
                const inner = (
                  <div className="card-surface flex h-full items-center gap-4 p-5 transition-all duration-300 hover:border-gold-500/40">
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gold-500/10 text-gold-300">
                      {card.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-charcoal-400">{card.label}</p>
                      <p className="truncate font-bold text-white" dir={card.label === 'الهاتف' || card.label === 'واتساب' || card.label === 'البريد الإلكتروني' ? 'ltr' : 'rtl'}>
                        {card.value}
                      </p>
                    </div>
                  </div>
                )
                return (
                  <Reveal key={card.label} delay={i * 0.08}>
                    {card.href ? (
                      <a
                        href={card.href}
                        target={card.href.startsWith('http') ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        className="block h-full"
                      >
                        {inner}
                      </a>
                    ) : (
                      inner
                    )}
                  </Reveal>
                )
              })}
            </div>

            {/* Quick actions */}
            <Reveal delay={0.2}>
              <div className="mt-6 flex flex-wrap gap-4">
                <a
                  href={`https://wa.me/${contact.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold flex-1"
                >
                  <WhatsappIcon className="h-5 w-5" />
                  واتساب
                </a>
                <a href={`tel:${contact.phoneHref}`} className="btn-outline flex-1">
                  <PhoneIcon className="h-5 w-5" />
                  اتصل الآن
                </a>
              </div>
            </Reveal>
          </div>

          {/* Message form */}
          <Reveal delay={0.1}>
            <div className="card-surface p-7 sm:p-8">
              <h3 className="mb-6 font-heading text-xl font-bold text-white">أرسل رسالة</h3>
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="ok"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center py-8 text-center"
                  >
                    <span className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold-gradient text-charcoal-950 shadow-gold">
                      <CheckIcon className="h-8 w-8" strokeWidth={2.5} />
                    </span>
                    <p className="font-heading text-lg font-bold text-white">
                      تم استلام رسالتك
                    </p>
                    <p className="mt-2 text-sm text-charcoal-300">
                      سنتواصل معك في أقرب وقت ممكن.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus('idle')}
                      className="btn-outline mt-6 !py-2.5 !text-sm"
                    >
                      إرسال رسالة أخرى
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label className="field-label" htmlFor="c-name">
                        الاسم *
                      </label>
                      <input
                        id="c-name"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                        className="field-input"
                        placeholder="اسمك الكريم"
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="c-email">
                        البريد الإلكتروني *
                      </label>
                      <input
                        id="c-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                        className="field-input"
                        placeholder="example@email.com"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="c-message">
                        الرسالة *
                      </label>
                      <textarea
                        id="c-message"
                        required
                        minLength={10}
                        rows={4}
                        value={form.message}
                        onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                        className="field-input resize-none"
                        placeholder="اكتب رسالتك هنا..."
                      />
                    </div>

                    {status === 'error' && (
                      <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                        {errorMsg || 'تعذّر إرسال الرسالة، يرجى المحاولة مرة أخرى.'}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {status === 'loading' ? 'جارٍ الإرسال...' : 'أرسل رسالة'}
                      {status !== 'loading' && <ArrowIcon className="h-5 w-5" />}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
